import type { AnchorHTMLAttributes } from 'react';

import { Icon } from '@onyx/ui/components/colleague/icon';
import { getClassName } from '@onyx/ui/style-utils';
import NextLink from 'next/link';

import styles from './link.module.css';

export type LinkVariant = 'filled' | 'outlined' | 'text' | 'destructive';
export type LinkDirection = 'forwards' | 'backwards';

export type LinkProps = Pick<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  | 'id'
  | 'onClick'
  | 'onFocus'
  | 'onBlur'
  | 'onKeyDown'
  | 'onKeyUp'
  | 'target'
  | 'rel'
  | 'title'
> & {
  children?: React.ReactNode;
  href: string;
  variant?: LinkVariant;
  direction?: LinkDirection;
  isDisabled?: boolean;
  isLoading?: boolean;
  isInverse?: boolean;
  hasChevron?: boolean;
  className?: string;
};

export const Link = ({
  children,
  href,
  variant = 'filled',
  direction = 'forwards',
  isDisabled = false,
  isLoading = false,
  isInverse = false,
  hasChevron = true,
  onClick,
  className,
  ...rest
}: LinkProps) => {
  const variantMap: Record<LinkVariant, string> = {
    filled: styles.variantFilled,
    outlined: styles.variantOutlined,
    text: styles.variantText,
    destructive: styles.variantDestructive,
  };

  const isInverseAllowed = variant !== 'destructive' && isInverse;
  const isBackwards = direction === 'backwards';

  const classNames = getClassName([
    styles.link,
    variantMap[variant],
    isInverseAllowed && styles.inverse,
    isDisabled && styles.disabled,
    isLoading && styles.loading,
    className,
  ]);

  const iconName = isBackwards ? 'NavigateBefore' : 'NavigateNext';
  const chevronIcon = !isLoading && hasChevron && (
    <Icon name={iconName} size={18} />
  );

  const loadingIcon = isLoading && (
    <Icon name="Sync" size={18} className={styles.loadingIcon} />
  );

  if (isDisabled || isLoading) {
    return (
      <span className={classNames} {...rest} aria-disabled="true">
        {isBackwards && chevronIcon}
        {loadingIcon}
        <span className={styles.label}>{children}</span>
        {!isBackwards && chevronIcon}
      </span>
    );
  }

  return (
    <NextLink href={href} className={classNames} onClick={onClick} {...rest}>
      {isBackwards && chevronIcon}
      {loadingIcon}
      <span className={styles.label}>{children}</span>
      {!isBackwards && chevronIcon}
    </NextLink>
  );
};
